import React, { useLayoutEffect } from 'react';
import { ScrollView, StyleSheet } from 'react-native';
import upperFirst from 'lodash/upperFirst';
import { Indicator } from 'design-system/Indicator';
import { rem } from 'design-system/values';
import { SettingsSection } from 'design-system/SettingsSection';
import { AppView } from 'design-system/AppView';
import { SettingsItem, SettingsItemSwitch } from 'design-system/SettingsItem';
import * as Icons from 'design-system/icons';
import { SelectAppIconModal } from 'features/app-icons';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import useColorScheme from 'hooks/useColorScheme';
import useColors from 'hooks/useColors';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useAppSelector } from 'store/hooks';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import { AppearanceModal } from '../appearance-settings/AppearanceModal';
import i18n from '../../../i18n';
import { isDesktop, isWeb } from '../../../constants';
import { useNotificationsPreferences } from '../notifications/hooks/useNotificationsPreferences';
const strings = {
    sectionName: 'Preferences',
    notifications: {
        title: 'Notifications',
        description: 'Get notified about important stuff',
    },
    appearance: {
        title: 'Appearance',
    },
    emmaIcons: {
        title: 'Emma Icons',
        description: 'Choose your favorite Emma icon',
    },
    organiseExperience: {
        title: 'Organise your experience',
        description: 'Customise your feed and bottom bar',
    },
    emmaReports: {
        title: 'Emma Reports',
        description: 'Receive insights from Emma',
    },
    emmaQuiz: {
        title: 'Emma Quiz',
        description: 'Join our weekly trivia quiz',
    },
};
export const PreferenceSettingsList = withAnimatedNavigationHeader(({ navigation, onScroll }) => {
    const userId = useAppSelector((state) => state.user.user.id);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => (<HelpScreenButton articlesKey={HELP_ARTICLES_KEY.SETTINGS}/>),
        });
    }, []);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const { navigateDeprecated } = useMainStackNavigation();
    const colors = useColors();
    const { feedPreferences, updatePreferences, isUpdating } = useNotificationsPreferences(true);
    const [state, setState] = React.useState(feedPreferences);
    React.useEffect(() => {
        if (!isUpdating) {
            setState(feedPreferences);
        }
    }, [isUpdating, feedPreferences]);
    const colorScheme = useColorScheme();
    const onValueChange = (value, preferenceKey) => {
        if (!preferenceKey)
            return;
        setState((prev) => {
            const newState = {
                ...prev,
                [preferenceKey]: value,
                // feed.report.monthly is in sync with feed.report.weekly
                'feed.report.monthly': preferenceKey === 'feed.report.weekly'
                    ? value
                    : prev['feed.report.weekly'],
            };
            updatePreferences(newState);
            return newState;
        });
    };
    if (userId === -1) {
        return (<AppView type="secondary" withFrame={false}>
          <Indicator dark/>
        </AppView>);
    }
    return (<AppView withFrame={false}>
        <ScrollView onScroll={onScroll} style={paddingHorizontalStyle} scrollEventThrottle={16}>
          <SettingsSection noMarginTop style={styles.marginTop}>
            <SettingsItem title={strings.notifications.title} description={strings.notifications.description} icon={<Icons.IconBell />} onPress={() => navigateDeprecated('Notifications')}/>
            {!isWeb && (<SettingsItem title={strings.appearance.title} description={colorScheme ? upperFirst(colorScheme) : ''} icon={<Icons.IconPaint />} onPress={() => AppearanceModal.show(colors)}/>)}
            {!isDesktop && !isWeb && (<SettingsItem title={strings.emmaIcons.title} description={i18n.t('chooseFavouriteIcon')} icon={<Icons.IconEmma />} onPress={SelectAppIconModal.show}/>)}
            {!isWeb && (<SettingsItem title={strings.organiseExperience.title} description={strings.organiseExperience.description} icon={<Icons.IconOrganize />} onPress={() => navigateDeprecated('Organize')}/>)}
            <SettingsItemSwitch value={state['feed.report.weekly']} itemKey="feed.report.weekly" title={strings.emmaReports.title} onValueChange={onValueChange} description={strings.emmaReports.description} icon={<Icons.IconActivity />}/>
            <SettingsItemSwitch value={state['feed.quiz']} itemKey="feed.quiz" onValueChange={onValueChange} title={strings.emmaQuiz.title} description={strings.emmaQuiz.description} icon={<Icons.IconTrophy />}/>
          </SettingsSection>
        </ScrollView>
      </AppView>);
}, ({ colors }) => ({
    title: strings.sectionName,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styles = StyleSheet.create({
    marginTop: {
        marginTop: rem(16),
    },
});
